import React, { useState } from 'react';

const NoTrafficClientsQuickActionModal = ({ record, onClose, onSave, currentUser }) => {
  const [formData, setFormData] = useState({
    action_type: '',
    description: '',
    new_status: record?.status || 'pendiente'
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const actionTypes = [
    { value: 'contact_attempted', label: '📞 Intento de Contacto', description: 'Se intentó contactar al cliente' },
    { value: 'email_sent', label: '📧 Email Enviado', description: 'Se envió un email al cliente' },
    { value: 'meeting_scheduled', label: '📅 Reunión Programada', description: 'Se programó una reunión' },
    { value: 'follow_up', label: '🔔 Seguimiento', description: 'Acción de seguimiento realizada' },
    { value: 'comment_added', label: '💬 Comentario', description: 'Agregar comentario o nota' },
    { value: 'status_changed', label: '🔄 Cambio de Estado', description: 'Cambiar el estado del registro' },
    { value: 'escalated', label: '⬆️ Escalado', description: 'Escalado a supervisor o manager' },
    { value: 'resolved', label: '✅ Resuelto', description: 'Problema resuelto exitosamente' }
  ];

  const statusOptions = [
    { value: 'pendiente', label: '⏳ Pendiente' },
    { value: 'en_proceso', label: '🔄 En Proceso' },
    { value: 'completado', label: '✅ Completado' },
    { value: 'cancelado', label: '❌ Cancelado' }
  ];

  const validateForm = () => {
    const newErrors = {};

    if (!formData.action_type) {
      newErrors.action_type = 'Selecciona un tipo de acción';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'La descripción es requerida';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const actionData = {
        ...formData,
        user_name: currentUser?.name || currentUser?.email || 'Sistema',
        old_status: record?.status,
        record_id: record?.id
      };

      await onSave(record.id, actionData);
    } catch (error) {
      console.error('Error al guardar acción:', error);
      alert('❌ Error al registrar la acción: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Limpiar error del campo cuando el usuario empiece a escribir
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const selectedActionType = actionTypes.find(type => type.value === formData.action_type);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-red-600 to-orange-600 text-white p-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold">⚡ Acción Rápida</h2>
              <p className="text-red-100 mt-1">
                Registrar acción para: {record?.nombre_cliente}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 text-2xl font-bold transition-colors"
            >
              ×
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Información del registro */}
            <div className="bg-gray-50 rounded-xl p-4">
              <h3 className="font-semibold text-gray-900 mb-2">📋 Información del Registro</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Cliente:</span>
                  <div className="font-medium">{record?.nombre_cliente}</div>
                </div>
                <div>
                  <span className="text-gray-600">Célula:</span>
                  <div className="font-medium">{record?.celula}</div>
                </div>
                <div>
                  <span className="text-gray-600">Estado Actual:</span>
                  <div className="font-medium">
                    {statusOptions.find(s => s.value === record?.status)?.label || record?.status}
                  </div>
                </div>
                <div>
                  <span className="text-gray-600">Campaña:</span>
                  <div className="font-medium">{record?.campana}</div>
                </div>
              </div>
            </div>

            {/* Tipo de acción */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Tipo de Acción *
              </label>
              <div className="grid grid-cols-1 gap-3">
                {actionTypes.map(type => (
                  <label
                    key={type.value}
                    className={`relative flex items-center p-4 border-2 rounded-xl cursor-pointer transition-all duration-200 ${
                      formData.action_type === type.value
                        ? 'border-red-500 bg-red-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="action_type"
                      value={type.value}
                      checked={formData.action_type === type.value}
                      onChange={handleChange}
                      className="sr-only"
                    />
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <span className="text-2xl">{type.label.split(' ')[0]}</span>
                        <div>
                          <div className="font-medium text-gray-900">
                            {type.label.substring(2)}
                          </div>
                          <div className="text-sm text-gray-500">
                            {type.description}
                          </div>
                        </div>
                      </div>
                    </div>
                    {formData.action_type === type.value && (
                      <div className="text-red-600">
                        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                  </label>
                ))}
              </div>
              {errors.action_type && <p className="text-red-500 text-sm mt-1">{errors.action_type}</p>}
            </div>

            {/* Cambio de estado (solo si se selecciona status_changed) */}
            {formData.action_type === 'status_changed' && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Nuevo Estado
                </label>
                <select
                  name="new_status"
                  value={formData.new_status}
                  onChange={handleChange}
                  className="w-full p-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200"
                >
                  {statusOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Descripción */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Descripción *
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder={selectedActionType ? 
                  `Describe los detalles de: ${selectedActionType.description}` : 
                  'Describe la acción realizada...'
                }
                rows="4"
                className={`w-full p-3 border-2 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200 ${
                  errors.description ? 'border-red-500' : 'border-gray-300'
                }`}
                required
              />
              {errors.description && <p className="text-red-500 text-sm mt-1">{errors.description}</p>}
            </div>

          </form>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t flex justify-end space-x-3">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || !formData.action_type}
            className="px-6 py-3 bg-gradient-to-r from-red-600 to-orange-600 text-white rounded-lg hover:from-red-700 hover:to-orange-700 font-medium transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
          >
            {loading ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Registrando...
              </div>
            ) : (
              '⚡ Registrar Acción'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NoTrafficClientsQuickActionModal;
